import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AppRoutes,
  DATASET_KEYS,
  DatasetKey,
  IntelligenceApiConfig,
  ModuleViewsRefreshService,
  SelectedDatasetService
} from '@mri-platform/dsg/core';
import { FeatureFlagsService } from '@mri-platform/shared/core';
import { DrawerItem, DrawerLink } from '@mri-platform/shared/ui-main-nav-bar';
import { catchError, combineLatest, map, Observable, of, startWith, switchMap } from 'rxjs';

const MODULE_VIEWS_WORKSPACE = 'PMX';
const MODULE_VIEWS_DATASETS: Record<DatasetKey, string> = {
  [DATASET_KEYS.commercial]: 'Commercial Data Model',
  [DATASET_KEYS.residential]: 'Residential Data Model'
};

export interface ModuleViewsResponse {
  views: { id: number; name: string; is_prebuilt: boolean }[];
  total: number;
}

@Injectable({ providedIn: 'root' })
export class ModuleViewsMenuService {
  private readonly baseUrl: string;

  constructor(
    private readonly http: HttpClient,
    private readonly featureFlagService: FeatureFlagsService,
    private readonly moduleViewsRefresh: ModuleViewsRefreshService,
    private readonly selectedDataset: SelectedDatasetService,
    intelligenceApiConfig: IntelligenceApiConfig
  ) {
    this.baseUrl = intelligenceApiConfig.url.trim();
  }

  getModuleViewLinks$(): Observable<DrawerItem[]> {
    return combineLatest([
      this.featureFlagService.isEnabled$('moduleViews'),
      this.selectedDataset.selected$,
      this.moduleViewsRefresh.changes$.pipe(startWith(undefined))
    ]).pipe(
      switchMap(([enabled, datasetKey]) =>
        enabled ? this.fetchModuleViewLinks$(MODULE_VIEWS_DATASETS[datasetKey]) : of([] as DrawerItem[])
      ),
      startWith([] as DrawerItem[])
    );
  }

  private fetchModuleViewLinks$(dataset: string): Observable<DrawerItem[]> {
    return this.http
      .get<ModuleViewsResponse>(`${this.baseUrl}/module-views`, {
        params: { workspace: MODULE_VIEWS_WORKSPACE, dataset }
      })
      .pipe(
        map(({ views }) => this.toViewSections(views)),
        catchError(() => of([] as DrawerItem[]))
      );
  }

  private toViewSections(views: ModuleViewsResponse['views']): DrawerItem[] {
    const toLink = (view: ModuleViewsResponse['views'][number]): DrawerLink => ({
      text: view.name,
      url: `/${AppRoutes.ModuleView.path}/${view.id}`
    });
    const prebuilt = views.filter(view => view.is_prebuilt).map(toLink);
    const mine = views.filter(view => !view.is_prebuilt).map(toLink);

    const sections: DrawerItem[] = [];
    if (prebuilt.length) {
      sections.push({ divider: true }, { text: 'MRI Views', links: prebuilt });
    }
    if (mine.length) {
      sections.push({ divider: true }, { text: 'My Views', links: mine });
    }
    return sections;
  }
}
